import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ArrowUpRight, Clock, AlertCircle, Info } from 'lucide-react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { getArticleBySlug, getRelatedArticles, type ArticleBlock } from '../components/articles';
import { PRODUCTS } from '../components/products';
import { Vial } from '../components/Vial';
import { CTA } from '../components/CTA';
function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}
function ProductInlineCard({
  productId,
  note
}: {
  productId: string;
  note?: string;
}) {
  const product = PRODUCTS.find(p => p.id === productId);
  if (!product) return null;
  return <Link to={`/shop/${product.slug}`} className="group not-prose my-7 flex items-center gap-5 rounded-2xl border border-border bg-card p-5 hover:border-foreground/20 transition-colors" data-id="element-896">
      <div className="h-20 w-16 rounded-xl bg-secondary/50 border border-border flex items-center justify-center flex-shrink-0 text-foreground" data-id="element-897">
        <Vial label="" doseMg={product.doseMg} volumeMl={product.volumeMl} category={product.category} size={56} showShadow={false} showHighlight={false} data-id="element-898" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-899">
        <div className="flex items-center gap-2 mb-1" data-id="element-900">
          <span className="text-[9.5px] uppercase tracking-wider font-medium text-muted-foreground" data-id="element-901">
            Mentioned in this article
          </span>
        </div>
        <div className="text-[16px] font-semibold tracking-tight text-foreground leading-tight" data-id="element-902">
          {product.name}
        </div>
        {note && <p className="mt-1 text-[12.5px] text-muted-foreground leading-snug" data-id="element-903">
            {note}
          </p>}
        <div className="mt-2 font-mono text-[11px] text-muted-foreground [font-feature-settings:'tnum']" data-id="element-904">
          <span className="text-foreground" data-id="element-905">{product.doseMg}</span>
          <span className="text-primary font-semibold" data-id="element-906">mg</span>
          <span className="text-border mx-1" data-id="element-907">·</span>
          <span className="text-primary" data-id="element-908">
            {product.purity.toFixed(1)}%
          </span>{' '}
          HPLC
        </div>
      </div>
      <div className="text-right flex-shrink-0" data-id="element-909">
        <div className="font-mono text-[18px] font-semibold text-foreground [font-feature-settings:'tnum']" data-id="element-910">
          £{product.price}
        </div>
        <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground group-hover:text-foreground transition-colors" data-id="element-911">
          View
          <ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} data-id="element-912" />
        </span>
      </div>
    </Link>;
}
function renderBlock(block: ArticleBlock, idx: number) {
  switch (block.type) {
    case 'paragraph':
      return <p key={idx} className="text-[16.5px] leading-[1.75] text-foreground/85 my-5" data-id="element-913">
          {block.text}
        </p>;
    case 'heading':
      {
        const level = block.level ?? 2;
        if (level === 3) {
          return <h3 key={idx} className="text-[20px] font-semibold tracking-[-0.015em] text-foreground mt-9 mb-3" data-id="element-914">
            {block.text}
          </h3>;
        }
        return <h2 key={idx} className="text-[26px] font-semibold tracking-[-0.02em] text-foreground mt-12 mb-3 leading-tight" data-id="element-915">
          {block.text}
        </h2>;
      }
    case 'list':
      {
        const Tag = block.ordered ? 'ol' : 'ul';
        return <Tag key={idx} className={`my-5 space-y-2.5 ${block.ordered ? 'list-decimal' : 'list-none'} pl-5`} data-id="element-916">
          {block.items.map((item, i) => <li key={i} className="text-[16px] leading-[1.7] text-foreground/85 relative" data-id="element-917">
              {!block.ordered && <span className="absolute -left-5 top-[12px] h-1.5 w-1.5 rounded-full bg-primary" aria-hidden data-id="element-918" />}
              {item}
            </li>)}
        </Tag>;
      }
    case 'quote':
      return <blockquote key={idx} className="my-8 border-l-2 border-primary pl-5 py-1" data-id="element-919">
          <p className="text-[18px] italic text-foreground leading-relaxed" data-id="element-920">
            &ldquo;{block.text}&rdquo;
          </p>
          {block.attribution && <footer className="mt-2 text-[12px] text-muted-foreground font-medium" data-id="element-921">
              — {block.attribution}
            </footer>}
        </blockquote>;
    case 'callout':
      {
        const isWarn = block.tone === 'warning';
        const Icon = isWarn ? AlertCircle : Info;
        return <aside key={idx} className={`my-7 flex items-start gap-3 px-5 py-4 rounded-xl border ${isWarn ? 'border-foreground/20 bg-foreground/[0.04]' : 'border-primary/30 bg-primary/[0.06]'}`} data-id="element-922">
          <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isWarn ? 'text-foreground' : 'text-primary'}`} strokeWidth={2} data-id="element-923" />
          <p className="text-[13.5px] leading-relaxed text-foreground/85" data-id="element-924">
            {block.text}
          </p>
        </aside>;
      }
    case 'productMention':
      return <ProductInlineCard key={idx} productId={block.productId} note={block.note} data-id="element-925" />;
    default:
      return null;
  }
}
export function ArticleDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  if (!article) return <Navigate to="/articles" replace data-id="element-926" />;
  const related = getRelatedArticles(article, 2);
  const relatedProducts = article.relatedProductIds.map(id => PRODUCTS.find(p => p.id === id)).filter(Boolean) as typeof PRODUCTS;
  return <div className="min-h-screen w-full bg-background" data-id="element-927">
      <AnnouncementBar data-id="element-928" />
      <Header data-id="element-929" />

      <main data-id="element-930">
        {/* Breadcrumb */}
        <section className="w-full border-b border-border" data-id="element-931">
          <div className="container mx-auto py-5" data-id="element-932">
            <nav className="flex items-center gap-1.5 text-[12px] text-muted-foreground" data-id="element-933">
              <Link to="/" className="hover:text-foreground transition-colors" data-id="element-934">
                Home
              </Link>
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-935" />
              <Link to="/articles" className="hover:text-foreground transition-colors" data-id="element-936">
                Journal
              </Link>
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-937" />
              <span className="text-foreground truncate" data-id="element-938">{article.title}</span>
            </nav>
          </div>
        </section>

        {/* Hero */}
        <section className="container mx-auto pt-12 lg:pt-16 pb-10" data-id="element-939">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.45
        }} className="max-w-3xl" data-id="element-940">
            <div className="flex items-center gap-3 mb-6" data-id="element-941">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-primary px-2.5 py-1 rounded-full bg-primary/10" data-id="element-942">
                {article.category}
              </span>
              <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground [font-feature-settings:'tnum']" data-id="element-943">
                <Clock className="h-3 w-3" strokeWidth={1.75} data-id="element-944" />
                {article.readMinutes} min read
              </span>
            </div>
            <h1 className="text-4xl lg:text-[52px] font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-945">
              {article.title}
            </h1>
            <p className="mt-5 text-[18px] text-muted-foreground leading-relaxed" data-id="element-946">
              {article.excerpt}
            </p>
            <div className="mt-7 flex items-center gap-3 text-[13px] flex-wrap" data-id="element-947">
              <div className="h-8 w-8 rounded-full bg-secondary border border-border flex items-center justify-center text-[12px] font-semibold text-foreground" data-id="element-948">
                {article.author.split(' ').map(s => s[0]).join('').slice(0, 2)}
              </div>
              <span className="text-foreground font-medium" data-id="element-949">
                {article.author}
              </span>
              <span className="text-border" data-id="element-950">·</span>
              <span className="text-muted-foreground" data-id="element-951">
                {article.authorRole}
              </span>
              <span className="text-border" data-id="element-952">·</span>
              <span className="font-mono text-muted-foreground" data-id="element-953">
                {formatDate(article.publishedAt)}
              </span>
            </div>
          </motion.div>
        </section>

        {/* Body — 2 column with sticky sidebar */}
        <section className="container mx-auto pb-20 lg:pb-28" data-id="element-954">
          <div className="grid lg:grid-cols-[1fr_320px] gap-12 lg:gap-16" data-id="element-955">
            {/* Article body */}
            <article className="max-w-2xl" data-id="element-956">
              {article.content.map((block, i) => renderBlock(block, i))}
            </article>

            {/* Sidebar — related products */}
            <aside className="lg:sticky lg:top-24 self-start space-y-6" data-id="element-957">
              {relatedProducts.length > 0 && <div className="rounded-2xl border border-border bg-card overflow-hidden" data-id="element-958">
                  <div className="px-5 pt-5 pb-3 border-b border-border" data-id="element-959">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-960">
                      Related products
                    </div>
                    <p className="mt-1.5 text-[13px] text-foreground font-semibold" data-id="element-961">
                      Peptides referenced in this article
                    </p>
                  </div>
                  <ul className="divide-y divide-border" data-id="element-962">
                    {relatedProducts.map(p => <li key={p.id} data-id="element-963">
                        <Link to={`/shop/${p.slug}`} className="flex items-center gap-3 px-5 py-3 hover:bg-secondary/40 transition-colors" data-id="element-964">
                          <div className="h-12 w-10 rounded-md bg-secondary/50 border border-border flex items-center justify-center flex-shrink-0 text-foreground" data-id="element-965">
                            <Vial label="" doseMg={p.doseMg} volumeMl={p.volumeMl} category={p.category} size={36} showShadow={false} showHighlight={false} data-id="element-966" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-967">
                            <div className="text-[13.5px] font-semibold tracking-tight text-foreground truncate" data-id="element-968">
                              {p.name}
                            </div>
                            <div className="font-mono text-[10.5px] text-muted-foreground mt-0.5 [font-feature-settings:'tnum']" data-id="element-969">
                              <span className="text-foreground" data-id="element-970">
                                {p.doseMg}
                              </span>
                              <span className="text-primary font-semibold" data-id="element-971">
                                mg
                              </span>{' '}
                              ·{' '}
                              <span className="text-primary" data-id="element-972">
                                {p.purity.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                          <span className="font-mono text-[13px] font-medium text-foreground flex-shrink-0 [font-feature-settings:'tnum']" data-id="element-973">
                            £{p.price}
                          </span>
                        </Link>
                      </li>)}
                  </ul>
                  <div className="p-4 border-t border-border bg-secondary/20" data-id="element-974">
                    <CTA to="/shop" variant="secondary" size="md" fullWidth withArrow data-id="element-975">
                      Browse all peptides
                    </CTA>
                  </div>
                </div>}

              {/* Tags */}
              {article.goals.length > 0 && <div className="rounded-2xl border border-border bg-card p-5" data-id="element-976">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-977">
                    Research goals
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-id="element-978">
                    {article.goals.map(g => <span key={g} className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary text-foreground text-[12px] font-medium border border-border" data-id="element-979">
                        {g}
                      </span>)}
                  </div>
                </div>}
            </aside>
          </div>
        </section>

        {/* Continue reading */}
        {related.length > 0 && <section className="w-full border-t border-border bg-secondary/30" data-id="element-980">
            <div className="container mx-auto py-16 lg:py-20" data-id="element-981">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-982">
                Continue reading
              </p>
              <h2 className="text-3xl lg:text-4xl font-semibold tracking-[-0.02em] text-foreground mb-8" data-id="element-983">
                More from the journal
              </h2>
              <div className="grid sm:grid-cols-2 gap-5 lg:gap-6" data-id="element-984">
                {related.map(a => <Link key={a.id} to={`/articles/${a.slug}`} className="group block rounded-2xl border border-border bg-background p-6 hover:border-foreground/20 transition-colors" data-id="element-985">
                    <div className="flex items-center gap-3 mb-4" data-id="element-986">
                      <span className="text-[10px] uppercase tracking-wider font-medium text-foreground px-2 py-0.5 rounded-full bg-card border border-border" data-id="element-987">
                        {a.category}
                      </span>
                      <span className="font-mono text-[10.5px] text-muted-foreground" data-id="element-988">
                        {a.readMinutes} min
                      </span>
                    </div>
                    <h3 className="text-[20px] font-semibold tracking-[-0.015em] text-foreground leading-tight group-hover:text-primary transition-colors" data-id="element-989">
                      {a.title}
                    </h3>
                    <p className="mt-2 text-[13.5px] text-muted-foreground line-clamp-2 leading-relaxed" data-id="element-990">
                      {a.excerpt}
                    </p>
                  </Link>)}
              </div>
            </div>
          </section>}
      </main>

      <Footer data-id="element-991" />
    </div>;
}